import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { getTestimonials } from '../api/products';
import client from '../api/client';

interface Testimonial {
    id: number;
    name: string;
    role?: string;
    content?: string;
    content_ar?: string;
    content_fr?: string;
    rating: number;
    avatar?: string;
    image?: string;
    audio?: string;
}

interface Props {
    productId?: number;
}

const TestimonialsCarousel: React.FC<Props> = ({ productId }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
    const [loading, setLoading] = useState(true);
    const [width, setWidth] = useState(0);
    const carouselRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                // Pass productId if it exists, otherwise it fetches general testimonials
                const data = await getTestimonials(productId);
                setTestimonials(data);
            } catch (error) {
                console.error('Failed to fetch testimonials', error);
                // Fallback data if API fails
                setTestimonials([
                    {
                        id: 1,
                        name: "Ahmed Benali",
                        role: "Verified Customer",
                        content: "Excellent quality products and fast delivery. Highly recommended!",
                        rating: 5
                    },
                    {
                        id: 2,
                        name: "Sarah Amrani",
                        role: "Loyal Client",
                        content: "I love the natural ingredients used. My skin feels amazing.",
                        rating: 5
                    },
                    {
                        id: 3,
                        name: "Karim Tazi",
                        role: "Verified Customer",
                        content: "Great customer service and packaging. Will order again.",
                        rating: 4
                    }
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, [productId]);

    useEffect(() => {
        if (carouselRef.current) {
            setWidth(carouselRef.current.scrollWidth - carouselRef.current.offsetWidth);
        }
    }, [testimonials]);

    if (loading) return null;

    return (
        <section className="py-20 bg-white overflow-hidden">
            <div className="container mx-auto px-4">
                <div className="text-center mb-16">
                    <motion.span
                        className="text-primary font-semibold tracking-wider uppercase text-sm"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        {t('testimonials.subtitle', 'What Our Clients Say')}
                    </motion.span>
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold text-dark mt-2"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                    >
                        {t('testimonials.title', 'Customer Testimonials')}
                    </motion.h2>
                </div>

                <motion.div
                    ref={carouselRef}
                    className="cursor-grab overflow-hidden"
                    whileTap={{ cursor: "grabbing" }}
                >
                    <motion.div
                        drag="x"
                        dragConstraints={{ right: 0, left: -width }}
                        className="flex gap-8 pb-8"
                    >
                        {testimonials.map((testimonial) => (
                            <motion.div
                                key={testimonial.id}
                                className="min-w-[300px] md:min-w-[400px] bg-neutral-50 p-8 rounded-2xl shadow-sm border border-neutral-100"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                            >
                                <div className="flex gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <svg
                                            key={i}
                                            className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>

                                {/* Audio Player */}
                                {testimonial.audio && (
                                    <div className="mb-4">
                                        <audio
                                            controls
                                            className="w-full h-10 rounded-full bg-neutral-100"
                                            controlsList="nodownload"
                                        >
                                            <source
                                                src={`${client.defaults.baseURL?.replace('/api', '')}/storage/${testimonial.audio}`}
                                                type="audio/mpeg"
                                            />
                                            {/* Fallback for other formats if needed, or error message */}
                                            Your browser does not support the audio element.
                                        </audio>
                                    </div>
                                )}

                                <p className="text-gray-600 mb-6 italic">
                                    "{isRTL ? (testimonial.content_ar || testimonial.content) : (testimonial.content_fr || testimonial.content)}"
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-xl overflow-hidden">
                                        {testimonial.image ? (
                                            <img
                                                src={`${client.defaults.baseURL?.replace('/api', '')}/storage/${testimonial.image}`}
                                                alt={testimonial.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            testimonial.name.charAt(0)
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-dark">{testimonial.name}</h4>
                                        <p className="text-sm text-gray-500">{testimonial.role}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default TestimonialsCarousel;
